import React, { useState, useEffect } from "react";
import { Text } from "@mantine/core";
import TagBadge, { tagBadgeColor, tagName } from "./TagBadge";

const manualTagOrder = [
  "like",
  "veryGood",
  "good",
  "finished",
  "bad",
  "inProgress",
  "todo",
];
const manualTagOrderIndex = manualTagOrder.reduce((acc, name, index) => {
  acc[name] = index;
  return acc;
}, {});

// Default view: personal recommendation (2), global selectable (3),
// global kind (4). "Show all tags" reveals custom / genre / subCategory /
// year — the auto-derived families sit behind collapsible [Жанр] /
// [Время] / [Свои тэги] triggers; subCategory (film/series) is only two
// chips so it renders inline without a group.
const alwaysVisibleTypes = new Set([2, 3, 4]);

const SelectableTags = ({ relevantTags, alreadySelectedTags = [], onSelectionChange, language }) => {
  const [selectedTags, setSelectedTags] = useState([]);
  const [showAllTags, setShowAllTags] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => new Set());

  useEffect(() => {
    setSelectedTags(alreadySelectedTags);
  }, [alreadySelectedTags]);

  const toggleTagSelection = (selectedTag) => {
    let updatedSelection;
    if (selectedTags.find((t) => t.tag.id === selectedTag.tag.id)) {
      updatedSelection = selectedTags.filter((t) => t.tag.id !== selectedTag.tag.id);
    } else {
      updatedSelection = [...selectedTags, selectedTag];
    }
    setSelectedTags(updatedSelection);
    onSelectionChange(updatedSelection);
  };

  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedTagIds = new Set(selectedTags.map((tagObject) => tagObject.tag.id));

  // Strict family order the main row must respect, even when a chip from a
  // later family is selected and thus pulled into mainList:
  //   rating (3) → personal recom (2) → kind (4) → subCategory (6) →
  //   genre (5) → year (7) → custom (1).
  const typePriority = { 3: 1, 2: 2, 4: 3, 6: 4, 5: 5, 7: 6, 1: 7 };
  const sortByFamily = (arr) => [...arr].sort((a, b) => {
    const aP = typePriority[a.tag.type] ?? 99;
    const bP = typePriority[b.tag.type] ?? 99;
    if (aP !== bP) return aP - bP;
    if (a.tag.type === 3 && b.tag.type === 3) {
      const aIndex = manualTagOrderIndex[a.tag.name] ?? 999;
      const bIndex = manualTagOrderIndex[b.tag.name] ?? 999;
      return aIndex - bIndex;
    }
    return 0;
  });

  // Main list: always-visible types + any selected chip (regardless of type
  // — a selected genre/year chip stays visible when its group is collapsed).
  const mainList = sortByFamily(
    relevantTags.filter((t) => alwaysVisibleTypes.has(t.tag.type) || selectedTagIds.has(t.tag.id))
  );

  // Behind "Show all tags": each family, minus anything already in mainList.
  const notInMain = (t) => !alwaysVisibleTypes.has(t.tag.type) && !selectedTagIds.has(t.tag.id);
  const subCategoryExtras = relevantTags.filter((t) => t.tag.type === 6 && notInMain(t));
  const genreExtras       = relevantTags.filter((t) => t.tag.type === 5 && notInMain(t));
  const yearExtras        = relevantTags.filter((t) => t.tag.type === 7 && notInMain(t));
  const customExtras      = relevantTags.filter((t) => t.tag.type === 1 && notInMain(t));

  const hasGenreGroup = relevantTags.some((t) => t.tag.type === 5);
  const hasYearGroup  = relevantTags.some((t) => t.tag.type === 7);
  const anyGenreSelected = selectedTags.some((t) => t.tag.type === 5);
  const anyYearSelected  = selectedTags.some((t) => t.tag.type === 7);

  const hasAdditionalTags = relevantTags.some(
    (tagObject) => !alwaysVisibleTypes.has(tagObject.tag.type)
  );

  const renderTagChip = (tagObject) => {
    const { name } = tagObject.tag;
    const { count } = tagObject;
    const isSelected = selectedTags.find((t) => t.tag.id === tagObject.tag.id);
    const selectedStyles = isSelected ? {
      root: { background: '#0A84FF', borderColor: 'transparent' },
      label: { color: '#ffffff' },
    } : undefined;
    return (
      <TagBadge
        key={tagObject.tag.id}
        component="button"
        type="button"
        className="cursor-pointer flex-shrink-0"
        size="md"
        styles={selectedStyles}
        onClick={() => toggleTagSelection(tagObject)}
      >
        {tagObject.tag.imgUrl !== "" ? (
          <div className="flex items-center text-center max-h-6 gap-1">
            <span>#</span>
            <img className="w-5 max-w-none h-5 rounded-full" src={tagObject.tag.imgUrl} />
            <span>{tagName(name, language)}</span>
            <Text span size="xs" c="inherit" style={{ opacity: 0.55, marginLeft: 4 }}>({count})</Text>
          </div>
        ) : (
          <>
            {tagName(name, language)}
            <Text span size="xs" c="inherit" style={{ opacity: 0.55, marginLeft: 4 }}>({count})</Text>
          </>
        )}
      </TagBadge>
    );
  };

  const renderGroupTrigger = (key, labelRu, labelEn) => {
    const open = openGroups.has(key);
    return (
      <TagBadge
        key={`group:${key}`}
        component="button"
        type="button"
        size="md"
        className="cursor-pointer flex-shrink-0"
        onClick={() => toggleGroup(key)}
      >
        {(language === "ru" ? labelRu : labelEn)}
        <span style={{ opacity: 0.55, marginLeft: 4, display: 'inline-block', transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none' }}>▾</span>
      </TagBadge>
    );
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 py-2">
        {mainList.map(renderTagChip)}
        {!showAllTags && hasAdditionalTags && (
          <TagBadge
            type="button"
            component="button"
            size="md"
            className="cursor-pointer flex-shrink-0"
            styles={{ label: { fontStyle: 'italic', opacity: 0.75 } }}
            onClick={() => setShowAllTags(true)}
          >
            + {language === "ru" ? "Показать все тэги" : "Show all tags"}
          </TagBadge>
        )}
        {showAllTags && (
          <>
            {subCategoryExtras.map(renderTagChip)}
            {hasGenreGroup && !anyGenreSelected && renderGroupTrigger("genre", "Жанр", "Genre")}
            {(openGroups.has("genre") || anyGenreSelected) && genreExtras.map(renderTagChip)}
            {hasYearGroup && !anyYearSelected && renderGroupTrigger("year", "Время", "Time")}
            {(openGroups.has("year") || anyYearSelected) && yearExtras.map(renderTagChip)}
            {customExtras.map(renderTagChip)}
            <TagBadge
              type="button"
              component="button"
              size="md"
              className="cursor-pointer flex-shrink-0"
              styles={{ label: { fontStyle: 'italic', opacity: 0.75 } }}
              onClick={() => setShowAllTags(false)}
            >
              − {language === "ru" ? "Скрыть" : "Hide"}
            </TagBadge>
          </>
        )}
      </div>
    </div>
  );
};

export default SelectableTags;
